<template>
    <b-modal id="contractor-excel-download-modal"
             hide-footer
             size="xl"
             :no-close-on-backdrop="downloadLoading"
             :no-close-on-esc="downloadLoading"
             @hide="closeModal"
             @shown="measureScrollbarWidth"
    >
        <template #modal-title>
            <div class="d-flex align-items-center" style="gap: 8px;">
                {{ $t('프로젝트별 엑셀 일괄 다운로드') }}
            </div>
        </template>

        <b-overlay :show="downloadLoading"
                   rounded="sm"
                   opacity="0.9"
                   bg-color="#efefef">
            <section class="mb-4">
                <b-form @submit.prevent="searchData">
                    <b-card no-body bg-variant="white">
                        <template #header>
                            <div class="d-flex align-items-center justify-content-between">
                                <h5 class="mb-0">{{ $t('참여 프로젝트 검색') }}</h5>
                                <div v-show="downloadLoading">
                                    <div class="d-flex align-items-center">
                                        <b-spinner class="mr-2" variant="primary" style="height: 1rem; width: 1rem;"/>
                                    </div>
                                </div>
                                <div v-show="!downloadLoading">
                                    <b-btn
                                        variant="primary"
                                        :disabled="searchLoading"
                                        type="submit"
                                    >
                                        {{ $t('검색하기') }}
                                    </b-btn>
                                </div>
                            </div>
                        </template>
                        <div class="p-3">
                            <b-row>
                                <b-col cols="8" class="mb-3 mb-xl-0">
                                    <b-form-group
                                        :label="$t('프로젝트 코드')"
                                        label-for="input-project_code"
                                        class="mb-0"
                                    >
                                        <b-form-input id="input-project_code"
                                                      v-model="searchFormValue.project_code"
                                                      :placeholder="$t('검색할 프로젝트 코드를 입력해주세요.')"
                                                      :disabled="downloadLoading"
                                                      trim
                                        />
                                    </b-form-group>
                                </b-col>
                                <b-col cols="4">
                                    <b-form-group
                                        :label="$t('분기')"
                                        label-for="input-period"
                                        class="mb-0"
                                    >
                                        <b-form-select id="input-period"
                                                       v-model="searchFormValue.period"
                                                       :disabled="downloadLoading"
                                        >
                                            <b-form-select-option :value="null">{{ $t('분기 선택') }}</b-form-select-option>
                                            <b-form-select-option v-for="period in periodList"
                                                                  :key="period.value"
                                                                  :value="period.value"
                                            >
                                                {{ period.text }}
                                            </b-form-select-option>
                                        </b-form-select>
                                    </b-form-group>
                                </b-col>
                            </b-row>
                        </div>
                    </b-card>
                </b-form>
            </section>

            <section>
                <div class="d-flex align-items-center justify-content-between mb-2">
                    <div class="list-info">
                        <div>{{ $t('전체 개수') }}: {{ total_num }}</div>
                        <span>/</span>
                        <div>{{ $t('선택된 프로젝트 개수') }}: {{ selected_num }}</div>
                    </div>
                    <div>
                        <b-button size="sm" variant="outline-dark"
                                  :disabled="downloadLoading || !selected_num"
                                  class="mr-2"
                                  @click="excelDownload('selected')"
                        >
                            {{ $t('엑셀 선택 다운로드') }}
                        </b-button>

                        <b-button size="sm"
                                  :disabled="downloadLoading || !listItem.length"
                                  @click="excelDownload('all')"
                        >
                            {{ $t('검색 결과 {n}개 전체 다운로드', { n: listItem.length }) }}
                        </b-button>
                    </div>
                </div>
                <b-overlay :show="searchLoading"
                           rounded="sm"
                           opacity="0.8"
                           bg-color="#efefef">
                    <b-card no-body>

                        <div class="virtual-table-wrapper">
                            <div class="header-container" :style="{ paddingRight: `${scrollbarWidth}px` }">
                                <table class="table b-table table-hover">
                                    <thead class="thead-light">
                                    <tr>
                                        <th v-for="field in fields"
                                            :key="field.key"
                                            :style="getColumnStyle(field)"
                                            :class="getThClass(field)"
                                        >
                                            <template v-if="field.key === 'selected'">
                                                <b-form-checkbox
                                                    :checked="allSelected"
                                                    :indeterminate="indeterminate"
                                                    :disabled="!listItem.length"
                                                    @change="toggleSelectAll"
                                                />

                                            </template>
                                            <template v-else>
                                                {{ $t(field.label) }}
                                            </template>
                                        </th>
                                    </tr>
                                    </thead>
                                </table>
                            </div>
                            <div v-if="listItem.length === 0" class="empty-container">
                                <h6 class="font-italic mb-0">{{ $t('검색된 프로젝트가 없습니다.') }}</h6>
                            </div>
                            <div v-else ref="bodyScrollContainer" class="body-scroll-container">
                                <RecycleScroller
                                    v-slot="{ item }"
                                    :items="listItem"
                                    :item-size="48.6"
                                    key-field="index"
                                >
                                    <table class="body-table">
                                        <tbody>
                                        <tr class="body-row">
                                            <td v-for="field in fields"
                                                :key="field.key"
                                                :style="getColumnStyle(field)"
                                                :class="getTdClass(field)"
                                            >
                                                <template v-if="field.key === 'selected'">
                                                    <b-form-checkbox
                                                        :checked="!!selected[item.project_code]"
                                                        @change="toggleSelect(item.project_code, $event)"
                                                    />
                                                </template>
                                                <template v-else-if="field.key === 'excel_download'">
                                                    <b-button size="sm" :disabled="downloadLoading"
                                                              variant="dark-border"
                                                              @click="excelDownload('single',item)"
                                                    >{{ $t('다운로드') }}
                                                    </b-button>
                                                </template>
                                                <template v-else-if="field.key === 'project_code'">
                                                    <span v-b-tooltip.hover="get_tooltip_title(item[field.key])">{{
                                                            item[field.key]
                                                        }}</span>
                                                </template>
                                                <template v-else-if="field.key === 'campaign_name'">
                                                    <span v-b-tooltip.hover="get_tooltip_title(item[field.key])">{{
                                                            item[field.key]
                                                        }}</span>
                                                </template>
                                                <template v-else-if="field.key === 'period'">
                                                    {{ periodText(item) }}
                                                </template>
                                                <template v-else-if="field.key === 'client'">
                                                    <span v-b-tooltip.hover="get_tooltip_title(item[field.key])">{{
                                                            item[field.key]
                                                        }}</span>
                                                </template>
                                                <template v-else>
                                                    {{ item[field.key] }}
                                                </template>
                                            </td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </RecycleScroller>
                            </div>
                        </div>
                    </b-card>
                </b-overlay>

            </section>

            <template #overlay>
                <div class="text-center">
                    <b-spinner size="small"></b-spinner>
                    <div class="mt-2">
                        {{ $t('요청하신 내용으로 엑셀을 다운로드 중입니다.') }}
                    </div>
                    <div>
                        {{ $t('잠시 기다려주세요...') }}
                    </div>
                </div>

            </template>
        </b-overlay>


    </b-modal>
</template>

<script>
import {mapGetters} from 'vuex';
import {columns} from '@shared/components/contractor/components/ContractorExcelDownload/data';
import FuzzySearch from 'fuzzy-search';
import dayjs from 'dayjs';
import downloadFile from '@shared/mixin/downloadFile';

export default {
    name: 'ContractorExcelDownloadModal',
    mixins: [
        downloadFile
    ],
    data() {
        return {
            fields: columns,
            searchFormValue: {
                project_code: '',
                period: null,
            },
            listItem: [],
            // 선택은 이 모달 안에서만 {project_code: true} 로 둔다(스토어 프로젝트 객체를 바꾸지 않음). 검색할 때마다 비운다
            selected: {},
            searchLoading: false,
            downloadLoading: false,
            // period 원문 → 그 분기의 첫 프로젝트(표기용 period_dis)
            periodObj: {},
            scrollbarWidth: 0
        };
    },
    computed: {
        ...mapGetters('contractor', ['Get_Contractor', 'GET_ProjectList']),
        // 최신순(연도 → 반기). '2025_1h' 는 [2025, 1] 로 비교한다
        periodList() {
            const keys = Object.keys(this.periodObj).sort((a, b) => {
                const [aYear, aHalf] = a.split('_').map(part => parseInt(part, 10) || 0);
                const [bYear, bHalf] = b.split('_').map(part => parseInt(part, 10) || 0);
                return bYear - aYear || bHalf - aHalf || b.localeCompare(a);
            });
            return keys.map(key => ({
                value: key,
                text: this.periodText(this.periodObj[key])
            }));
        },
        // 선택 수·전체 선택은 지금 보이는 검색 결과 기준
        selectedCodes() {
            return this.listItem.filter(item => this.selected[item.project_code]).map(item => item.project_code);
        },
        selected_num() {
            return this.selectedCodes.length;
        },
        allSelected() {
            return this.listItem.length > 0 && this.selected_num === this.listItem.length;
        },
        indeterminate() {
            return this.selected_num > 0 && this.selected_num < this.listItem.length;
        },
        get_tooltip_title() {
            return (data) => {
                return data ? {title: data, placement: 'topright'} : null;
            };
        },
        total_num() {
            return this.GET_ProjectList.length;
        },
    },
    watch: {
        listItem() {
            this.measureScrollbarWidth();
        },
        GET_ProjectList: {
            immediate: true,
            handler() {
                this.updateFuzzySearch();
            }
        }
    },
    mounted() {
        this.$root.$on('bv::tooltip::show', this.onTooltipShow);
    },
    beforeDestroy() {
        this.$root.$off('bv::tooltip::show', this.onTooltipShow);
    },
    methods: {
        updateFuzzySearch() {
            const periodObj = {};
            this.GET_ProjectList.forEach(item => {
                if (!periodObj[item.period]) periodObj[item.period] = item;
            });
            this.periodObj = periodObj;
            this.searchData();
        },
        periodText(project) {
            return project.period_dis?.[this.$i18n.locale] || project.period;
        },
        closeModal(event) {
            if (this.downloadLoading) {
                event.preventDefault();
                return;
            }
            this.$bvModal.hide('contractor-excel-download-modal');
        },
        // 목록이 바뀌면(프로젝트 목록 갱신 포함) 검색기를 새로 만든다. 숨은 항목이 선택된 채 남지 않게 선택을 비운다
        searchData() {
            this.selected = {};
            const filteredList = this.GET_ProjectList.filter(item => {
                return this.searchFormValue.period === null || item.period === this.searchFormValue.period;
            });
            const tempResult = new FuzzySearch(filteredList, ['project_code'], { caseSensitive: false })
                .search(this.searchFormValue.project_code);
            this.listItem = tempResult.map((item, index) => {
                return {
                    ...item,
                    index: tempResult.length - index,
                    target_end_date: item.target_end_date ? item.target_end_date.substring(0, 10) : '',
                };
            });
        },
        toggleSelectAll(checked) {
            this.selected = checked
                ? Object.fromEntries(this.listItem.map(item => [item.project_code, true]))
                : {};
        },
        toggleSelect(code, checked) {
            if (checked) this.$set(this.selected, code, true);
            else this.$delete(this.selected, code);
        },
        // 모달이 보이고 목록이 그려진 뒤에만 잴 수 있다(@shown, listItem 변경)
        measureScrollbarWidth() {
            this.$nextTick(() => {
                const el = this.$refs.bodyScrollContainer;
                this.scrollbarWidth = el ? el.offsetWidth - el.clientWidth : 0;
            });
        },
        getColumnStyle(field) {
            const style = {};
            if (field.width) {
                style.width = typeof field.width === 'number' ? `${field.width}px` : field.width;
            }
            if (field.hAlign) style.textAlign = field.hAlign;
            if (field.vAlign) style.verticalAlign = field.vAlign === 'center' ? 'middle' : field.vAlign;
            return style;
        },
        getThClass(field) {
            return field.thClass || '';
        },
        getTdClass(field) {
            return field.tdClass || '';
        },
        async excelDownload(type, item) {
            if (this.downloadLoading) return;
            let project_codes = [];
            if (type === 'single' && item) {
                project_codes = [item.project_code];
            } else if (type === 'selected') {
                project_codes = this.selectedCodes;
            } else if (type === 'all') {
                // '전체'는 지금 보이는 검색 결과 전체
                project_codes = this.listItem.map(_item => _item.project_code);
            }
            if (project_codes.length === 0) {
                this.$showModal({
                    type: 'alert',
                    state: 'warning',
                    title: this.$t('엑셀 다운로드'),
                    message: this.$t('다운로드할 항목이 없습니다.'),
                });
                return;
            }
            // 여러 프로젝트는 파일이 크고 오래 걸리므로 건수를 보여 주고 확인받는다
            if (project_codes.length > 1) {
                const _confirmed = await this.$showModal({
                    type: 'confirm',
                    state: 'info',
                    title: this.$t('엑셀 다운로드'),
                    message: this.$t('프로젝트 {n}개의 엑셀을 내려받습니다. 계속하시겠습니까?', { n: project_codes.length }),
                    modalOptions: { cancelTitle: this.$t('취소') }
                });
                if (_confirmed !== true) return;
            }

            try {
                this.downloadLoading = true;
                const _res_participants_excel = await this.$axios({
                    method: 'post',
                    url: '/projects/export-excel',
                    responseType: 'blob',
                    data: {
                        project_codes: project_codes,
                    }
                });
                this.downloadFileFromResponse(_res_participants_excel, `${this.$t('참여기업_진행현황_데이터')}_${dayjs().format('YYYY_MM_DD_HH-mm')}`);
                this.$EventBus.$emit('toast-success', this.$t('엑셀 다운로드'), this.$t('엑셀 다운로드를 완료했습니다.'));

            } catch (e) {
                // 401 은 인터셉터가 로그인 화면으로 보낸다
                if (e?.response?.status === 401) return;
                const h = this.$createElement;

                console.error('Error downloading Excel:', e);
                console.dir(e);
                this.$showModal({
                    type: 'alert',
                    state: 'error',
                    title: this.$t('엑셀 다운로드 에러'),
                    message: h('div', {}, [
                        h('p', { class: 'mb-1' }, this.$t('엑셀 다운로드 중 오류가 발생했습니다.')),
                        h('p', { class: 'mb-0' }, this.$t('지속적으로 문제 발생 시, 관리자에게 문의해주세요.')),
                    ]),
                });

            } finally {
                this.downloadLoading = false;
            }
        },
        onTooltipShow(bvEvent) {
            const targetEl = bvEvent.target;

            const tooltip = bvEvent.vueTarget;
            if (!tooltip) return;

            const width = targetEl.offsetWidth;
            tooltip.offset = -(width / 2);

        },


    },
};
</script>

<style lang="scss" scoped>
.empty-container {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 50vh;
    padding: 2rem;
    color: #6c757d;
    font-style: italic;
}

.list-info {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.875rem;
    color: #6c757d;
}
</style>
